import apiClient from '@/lib/api-client';
import type { AvailabilityOverride } from './availability.service';

// Types for the availability grid
export interface ProviderAvailabilityBlock {
  id: number;
  start_time: string; // "09:00"
  end_time: string;   // "17:00"
  recurrence_type: 'weekly' | 'biweekly' | 'monthly';
  recurrence_group_id: string | null;
  services: { id: number; name: string }[];
}

export interface ProviderBookingBlock {
  id: number;
  public_id: string;
  start_time: string;
  end_time: string;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';
  customer_name: string;
  customer_email: string;
  service_name: string;
  service_id: number;
}

export interface ProviderOverrideBlock {
  id: number;
  type: 'unavailable' | 'modified' | 'extra';
  start_time: string | null;
  end_time: string | null;
  reason: string | null;
  services: { id: number; name: string }[];
}

export interface ProviderCalendarBlock {
  id: number;
  start_time: string; // "14:00"
  end_time: string;   // "15:30"
  reason: string;
}

// One materialised group-class instance for a specific date — backed by an
// AvailableSlot row, so it carries live capacity counters and a stable id
// suitable for editing/cancelling the class.
export interface ProviderGroupClassSlotBlock {
  id: number;
  start_time: string; // "18:00"
  end_time: string;   // "19:00"
  service: { id: number; name: string };
  max_capacity: number;
  current_bookings: number;
  cancelled?: boolean;
  cancelled_at?: string | null;
}

// Detailed view for the edit dialog: slot + the participants list.
export interface GroupClassParticipant {
  id: number;
  public_id: string;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';
  customer_name: string;
  customer_email: string;
  created_at: string;
  // Batch this participant was enrolled with (null when booked standalone).
  booking_series_id?: string | null;
  // This participant's upcoming bookings in that series (drives batch actions).
  series_future_count?: number;
}

export interface GroupClassSlotDetail {
  id: number;
  date: string;       // "2026-05-06"
  start_time: string; // "18:00"
  end_time: string;   // "19:00"
  service: { id: number; name: string; duration_minutes: number };
  provider_id: number;
  provider?: { id: number; display_name: string | null };
  max_capacity: number;
  current_bookings: number;
  waitlist_count?: number;
  waitlist_enabled?: boolean;
  cancelled?: boolean;
  cancelled_at?: string | null;
  // Set when the class was materialised from a recurring rule. `series_remaining`
  // counts this occurrence plus every later one still on the schedule — what a
  // "cancel this and all following" would take out.
  recurring?: boolean;
  series_remaining?: number;
  participants: GroupClassParticipant[];
}

export type GroupClassCancelScope = 'single' | 'series';

export interface GroupClassCancelResult {
  id: number;
  cancelled: boolean;
  // True when nothing is left on the calendar for the cancelled occurrences —
  // no follow-up "remove from schedule" is needed.
  removed: boolean;
  cancelled_count: number;
  series_ended: boolean;
  participants_notified: number;
  waitlist_notified?: number;
}

export interface GroupClassProviderOption {
  id: number;
  display_name: string | null;
  available: boolean;
  reason: string | null;
}

export interface ProviderDayData {
  availabilities: ProviderAvailabilityBlock[];
  bookings: ProviderBookingBlock[];
  overrides: ProviderOverrideBlock[];
  calendar_blocks: ProviderCalendarBlock[];
  group_class_slots?: ProviderGroupClassSlotBlock[];
}

export interface GridProvider {
  id: number;
  display_name: string;
  title: string;
  avatar_url: string | null;
  color: string;
  active: boolean;
  days: Record<string, ProviderDayData>;
}

export interface AvailabilityGridResponse {
  start_date: string;
  end_date: string;
  providers: GridProvider[];
}

export interface GridQueryParams {
  startDate: string;
  endDate: string;
  providerIds?: number[];
}

// Types for creating availability
export type RecurrenceType = 'weekly' | 'biweekly' | 'monthly';
export type EndType = 'never' | 'on_date' | 'after_occurrences';

export interface CreateRecurringParams {
  provider_id: number;
  days_of_week: number[];
  start_time: string;
  end_time: string;
  recurrence_type: RecurrenceType;
  recurrence_interval?: number;
  monthly_week?: number | null;
  monthly_day?: number | null;
  end_type: EndType;
  end_date?: string | null;
  occurrences?: number | null;
  service_ids?: number[];
  force_override?: boolean;
}

export interface CreateDateRangeParams {
  provider_id: number;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  reason?: string;
  service_ids?: number[];
}

export interface CreateOneTimeParams {
  provider_id: number;
  date: string;
  start_time: string;
  end_time: string;
  reason?: string;
  service_ids?: number[];
}

// Group class — recurring rule. End time is auto-computed server-side from
// service.duration_minutes; client only sends start_time.
export interface CreateGroupClassRecurringParams {
  provider_id: number;
  service_id: number;
  days_of_week: number[];
  start_time: string;
  recurrence_type: RecurrenceType;
  recurrence_interval?: number;
  monthly_week?: number | null;
  monthly_day?: number | null;
  end_type: EndType;
  end_date?: string | null;
  occurrences?: number | null;
  effective_from?: string | null;
}

export interface CreateGroupClassOneTimeParams {
  provider_id: number;
  service_id: number;
  date: string;
  start_time: string;
}

export interface GroupClassRecurringResponse {
  blocks: ProviderAvailabilityBlock[];
  group_id: string;
  slots_created: number;
}

// Full availability details (returned by show endpoint)
export interface ProviderAvailabilityDetails {
  id: number;
  provider_id: number;
  day_of_week: number;
  day_name: string;
  start_time: string;
  end_time: string;
  formatted_range: string;
  duration_minutes: number;
  active: boolean;
  recurrence_type: RecurrenceType;
  recurrence_interval: number;
  monthly_week: number | null;
  monthly_day: number | null;
  end_type: EndType;
  end_date: string | null;
  effective_from: string | null;
  occurrences: number | null;
  recurrence_group_id: string | null;
  recurrence_summary: string;
  services: { id: number; name: string }[];
  service_ids: number[];
  created_at: string;
  updated_at: string;
}

class AdminAvailabilityService {
  /**
   * Get a single availability by ID (for any provider in the organization)
   */
  async getAvailability(id: number): Promise<ProviderAvailabilityDetails> {
    const { data } = await apiClient.get<{ data: ProviderAvailabilityDetails }>(
      `/v1/admin/availability/${id}`
    );
    return data.data;
  }

  /**
   * Get availability grid data for all providers
   * Returns availabilities, bookings, and overrides for each provider/day combination
   */
  async getGrid(params: GridQueryParams): Promise<AvailabilityGridResponse> {
    const queryParams: Record<string, string | string[]> = {
      start_date: params.startDate,
      end_date: params.endDate,
    };

    if (params.providerIds?.length) {
      queryParams.provider_ids = params.providerIds.map(String);
    }

    const { data } = await apiClient.get<{ data: AvailabilityGridResponse }>(
      '/v1/admin/availability/grid',
      { params: queryParams }
    );

    return data.data;
  }

  /**
   * Create recurring availability for a provider
   */
  async createRecurringAvailability(params: CreateRecurringParams): Promise<ProviderAvailabilityBlock[]> {
    const { data } = await apiClient.post<{ data: ProviderAvailabilityBlock[] }>(
      '/v1/admin/availability/recurring',
      params
    );
    return data.data;
  }

  /**
   * Create date range availability for a provider
   */
  async createDateRangeAvailability(params: CreateDateRangeParams): Promise<ProviderOverrideBlock[]> {
    const { data } = await apiClient.post<{ data: ProviderOverrideBlock[] }>(
      '/v1/admin/availability/date_range',
      params
    );
    return data.data;
  }

  /**
   * Create one-time availability for a provider
   */
  async createOneTimeAvailability(params: CreateOneTimeParams): Promise<ProviderOverrideBlock> {
    const { data } = await apiClient.post<{ data: ProviderOverrideBlock }>(
      '/v1/admin/availability/one_time',
      params
    );
    return data.data;
  }

  /**
   * Create a recurring group-class rule. The server materialises one
   * AvailableSlot per occurrence (max_capacity from the service), so the
   * schedule shows empty 0/N classes immediately on success.
   */
  async createGroupClassRecurringAvailability(
    params: CreateGroupClassRecurringParams
  ): Promise<GroupClassRecurringResponse> {
    const { service_id, ...rest } = params;
    const { data } = await apiClient.post<{
      data: ProviderAvailabilityBlock[];
      meta?: { group_id?: string; slots_created?: number };
    }>('/v1/admin/availability/recurring', {
      ...rest,
      kind: 'group_class',
      service_ids: [service_id],
    });
    return {
      blocks: data.data,
      group_id: data.meta?.group_id ?? '',
      slots_created: data.meta?.slots_created ?? 0,
    };
  }

  /**
   * Materialise a single one-off group-class slot for a given date+time.
   */
  async createGroupClassOneTimeAvailability(
    params: CreateGroupClassOneTimeParams
  ): Promise<{ id: number; start_time: string; end_time: string; max_capacity: number }> {
    const { data } = await apiClient.post<{
      data: { id: number; start_time: string; end_time: string; max_capacity: number };
    }>('/v1/admin/availability/group_class_one_time', params);
    return data.data;
  }

  /**
   * Cancel a materialised group-class instance — or, with scope 'series', that
   * instance and every later one from the same recurring rule. Active bookings
   * are cancelled and participants emailed. A class nobody had booked leaves the
   * schedule straight away (`removed: true`); one with participants is marked
   * cancelled and swept up shortly after, once the emails have gone out.
   */
  async cancelGroupClassSlot(
    slotId: number,
    scope: GroupClassCancelScope = 'single',
  ): Promise<GroupClassCancelResult> {
    const { data } = await apiClient.delete<{ data: GroupClassCancelResult }>(
      `/v1/admin/availability/group_class_slots/${slotId}`,
      { params: { scope } },
    );
    return data.data;
  }

  /**
   * Hard-delete a previously-cancelled group class. Refused if the slot
   * has not been cancelled first; succeeds (idempotently) if it is already gone.
   */
  async removeGroupClassSlot(slotId: number): Promise<{ id: number; removed: boolean }> {
    const { data } = await apiClient.delete<{ data: { id: number; removed: boolean } }>(
      `/v1/admin/availability/group_class_slots/${slotId}/remove`
    );
    return data.data;
  }

  async getGroupClassSlot(slotId: number): Promise<GroupClassSlotDetail> {
    const { data } = await apiClient.get<{ data: GroupClassSlotDetail }>(
      `/v1/admin/availability/group_class_slots/${slotId}`
    );
    return data.data;
  }

  /**
   * Providers who offer this class's service, each flagged with whether they're
   * free at the given date/start_time (defaults to the slot's current time).
   * Used by the edit dialog's provider picker.
   */
  async getGroupClassProviderOptions(
    slotId: number,
    params?: { date?: string; start_time?: string }
  ): Promise<GroupClassProviderOption[]> {
    const { data } = await apiClient.get<{ data: { providers: GroupClassProviderOption[] } }>(
      `/v1/admin/availability/group_class_slots/${slotId}/provider_options`,
      { params }
    );
    return data.data.providers;
  }

  async updateGroupClassSlot(
    slotId: number,
    params: { date?: string; start_time?: string; provider_id?: number }
  ): Promise<GroupClassSlotDetail> {
    const { data } = await apiClient.patch<{ data: GroupClassSlotDetail }>(
      `/v1/admin/availability/group_class_slots/${slotId}`,
      params
    );
    return data.data;
  }
  /**
   * Update availability for future occurrences (for any provider in the organization)
   */
  async updateFutureAvailability(
    id: number,
    data: {
      day_of_week: number;
      start_time: string;
      end_time: string;
      recurrence_type: string;
      recurrence_interval: number;
      monthly_week: number | null;
      monthly_day: number | null;
      end_type: string;
      end_date: string | null;
      occurrences: number | null;
      service_ids?: number[];
    }
  ): Promise<ProviderAvailabilityDetails> {
    const { data: response } = await apiClient.put<{ data: ProviderAvailabilityDetails }>(
      `/v1/admin/availability/${id}/update_future`,
      data
    );
    return response.data;
  }

  /**
   * Delete an availability (for any provider in the organization)
   */
  async deleteAvailability(id: number): Promise<void> {
    await apiClient.delete(`/v1/admin/availability/${id}`);
  }

  /**
   * Update all availabilities in a recurrence group
   */
  async updateGroup(
    groupId: string,
    data: {
      start_time?: string;
      end_time?: string;
      recurrence_type?: string;
      recurrence_interval?: number;
      end_type?: string;
      end_date?: string | null;
      occurrences?: number | null;
      service_ids?: number[];
      days_of_week?: number[];
    }
  ): Promise<any> {
    const { data: response } = await apiClient.put(
      `/v1/admin/availability/group/${groupId}`,
      data
    );
    return response.data;
  }

  /**
   * End a recurrence group from a specific date forward
   */
  async endGroup(groupId: string, date: string): Promise<any> {
    const { data } = await apiClient.put(
      `/v1/admin/availability/group/${groupId}/end`,
      { date }
    );
    return data.data;
  }

  /**
   * Delete all availabilities in a recurrence group
   */
  async deleteGroup(groupId: string): Promise<void> {
    await apiClient.delete(`/v1/admin/availability/group/${groupId}`);
  }

  /**
   * Get a single override by ID (for any provider in the organization)
   */
  async getOverride(id: number): Promise<AvailabilityOverride> {
    const { data } = await apiClient.get<{ data: AvailabilityOverride }>(
      `/v1/admin/availability/overrides/${id}`
    );
    return data.data;
  }

  /**
   * Update an override (for any provider in the organization)
   */
  async updateOverride(
    id: number,
    data: {
      date?: string;
      start_time: string;
      end_time: string;
      reason?: string;
      service_ids?: number[];
    }
  ): Promise<AvailabilityOverride> {
    const { data: response } = await apiClient.put<{ data: AvailabilityOverride }>(
      `/v1/admin/availability/overrides/${id}`,
      data
    );
    return response.data;
  }

  /**
   * Delete an override (for any provider in the organization)
   */
  async deleteOverride(id: number): Promise<void> {
    await apiClient.delete(`/v1/admin/availability/overrides/${id}`);
  }

  /**
   * Mark a date as unavailable for a provider (used for "delete this day only" on recurring availability)
   */
  async markDateUnavailable(providerId: number, date: string, reason?: string, recurrenceGroupId?: string, startTime?: string, endTime?: string): Promise<void> {
    await apiClient.post('/v1/admin/availability/mark_unavailable', {
      provider_id: providerId,
      date,
      reason,
      recurrence_group_id: recurrenceGroupId,
      start_time: startTime,
      end_time: endTime,
    });
  }
}

export const adminAvailabilityService = new AdminAvailabilityService();
export default adminAvailabilityService;
